import { useState, useEffect, useCallback } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { sortClientsByRecentlyViewed } from '@/db/services/client_service';
import { useCurrentPerson } from '../hooks/useCurrentPerson';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import { dispatchDataEvent, DataEvents } from '@/db/events';
import db from '@/db/db';
import ClientsList from '../components/ClientsList';
import PullToRefreshIndicator from '../components/PullToRefreshIndicator';
import Modal from '../components/Modal';
import DocumentTemplateModal from '../components/DocumentTemplateModal';

export default function Clients() {
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [newDocClient, setNewDocClient] = useState<{ id: number; slug: string } | null>(null);

  // Get current person for organization scoping
  const { organizationId, currentPerson } = useCurrentPerson();

  // Use useLiveQuery to get clients from the database
  const clients = useLiveQuery(async () => {
    if (!organizationId) return [];

    const allClients = await db.clients
      .where('organization_id')
      .equals(organizationId)
      .and(client => client.id !== 0) // Exclude metadata record
      .toArray();

    return sortClientsByRecentlyViewed(allClients);
  }, [organizationId]);

  const loading = clients === undefined;

  // Handle refresh for pull-to-refresh
  const handleRefresh = useCallback(async () => {
    if (!isOnline) return;

    await dispatchDataEvent(DataEvents.REFRESH_CLIENTS);
  }, [isOnline]);

  // Pull-to-refresh hook
  const { pullDistance, isRefreshing } = usePullToRefresh({
    onRefresh: handleRefresh,
    isEnabled: isOnline,
  });

  // Network status monitoring
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Refresh clients from server when page loads
  useEffect(() => {
    if (currentPerson?.organization.id && navigator.onLine) {
      dispatchDataEvent(DataEvents.REFRESH_CLIENTS)
        .catch(err => {
          console.error('Failed to refresh clients on page load:', err);
        });
    }
  }, [currentPerson?.organization.id]);

  return (
    <div className="min-h-full bg-gray-50">
      {/* Pull to Refresh Indicator */}
      <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={isRefreshing} />

      <div className="px-6 py-6 space-y-5">
        {/* Clients List (includes search) */}
        {loading ? (
          <div className="py-16 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-deep-sea-green mx-auto mb-4" />
            <p className="text-sm text-gray-500">Loading clients...</p>
          </div>
        ) : (
          <ClientsList
            clients={clients || []}
            onCantFindClient={() => setIsModalOpen(true)}
            onNewDocument={(id, slug) => setNewDocClient({ id, slug })}
          />
        )}
      </div>

      {/* Can't Find Client Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Can't find the client you are looking for?"
        maxWidth="2xl"
      >
        <div>
          <p className="text-sm font-semibold text-gray-600 mb-4">
            The client isn't assigned to you
          </p>
          <div className="text-sm font-normal text-gray-800">
            <p className="mb-6">
              The offline app only shows clients assigned to you online.
            </p>
            <ol type="1" className="list-decimal ml-4 mb-4">
              <li>Go to My Clients in the online app</li>
              <li>Look for the client in the list</li>
              <li>If the client isn't there, assign the client through a program enrollment (or ask your supervisor to do that for you)</li>
              <li>Once assigned, the client will download for offline use</li>
            </ol>
          </div>
        </div>
      </Modal>

      {/* New Document Modal — triggered from client list */}
      {newDocClient && (
        <DocumentTemplateModal
          isOpen={true}
          onClose={() => setNewDocClient(null)}
          clientId={newDocClient.id}
          clientSlug={newDocClient.slug}
        />
      )}
    </div>
  );
}
